import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionSuccess() {
  const { token, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [result, setResult] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (sessionId && token) {
      verifyCheckout(sessionId);
    } else if (!token) {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, token]);

  const verifyCheckout = async (sessionId) => {
    try {
      const response = await fetch(`${API}/subscription/verify-checkout?session_id=${sessionId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        toast.success("Subscription activated!");
        // Refresh user data to get updated tier
        await checkAuth();
      } else {
        toast.error(data.message || "Verification failed");
        setResult({ success: false, message: data.message });
      }
    } catch (error) {
      toast.error("Failed to verify subscription");
      setResult({ success: false, message: "Verification error" });
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Verifying your subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl p-8 max-w-md w-full text-center">
        {result?.success ? (
          <>
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
            <h1 className="font-heading text-2xl font-bold mb-2">
              Welcome to {result.plan?.charAt(0).toUpperCase() + result.plan?.slice(1)}!
            </h1>
            <p className="text-muted-foreground mb-6">
              {result.message}
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              Your subscription is now active. You have full access to all features included in your plan.
            </p>
            <Button onClick={() => navigate("/dashboard")} className="w-full" data-testid="go-to-dashboard-btn">
              Go to Dashboard
            </Button>
          </>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-bold mb-2">
              Something went wrong
            </h1>
            <p className="text-muted-foreground mb-6">
              {result?.message || "We couldn't verify your subscription. Please try again or contact support."}
            </p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate("/pricing")} className="flex-1">
                Try Again
              </Button>
              <Button onClick={() => navigate("/dashboard")} className="flex-1">
                Dashboard
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
